import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { BookMarked, Loader2, Plus, Trash2, CheckCircle2, Save, MapPin } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function RateInput({ value, onChange, testid }) {
  return (
    <div className="flex items-center rounded border border-input bg-popover px-1 w-[76px]">
      <span className="text-muted-foreground text-xs">$</span>
      <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent px-1 py-1 text-sm font-mono text-right outline-none" />
    </div>
  );
}

function Row({ e, onSaved }) {
  const [r, setR] = useState({ labor_rate: e.labor_rate, material_rate: e.material_rate, equipment_rate: e.equipment_rate });
  const [busy, setBusy] = useState(false);
  const dirty = Number(r.labor_rate) !== e.labor_rate || Number(r.material_rate) !== e.material_rate || Number(r.equipment_rate) !== e.equipment_rate;
  const patch = async (payload, msg) => {
    setBusy(true);
    try { await api.patch(`/price-book/${e.id}`, payload); toast.success(msg); onSaved(); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  const del = async () => { try { await api.delete(`/price-book/${e.id}`); toast.success("Removed"); onSaved(); } catch (err) { toast.error(apiErr(err)); } };
  const unit = (Number(r.labor_rate) || 0) + (Number(r.material_rate) || 0) + (Number(r.equipment_rate) || 0);
  return (
    <div className="grid grid-cols-[1fr_84px_84px_84px_90px_auto] items-center gap-2 rounded-lg border border-border/60 px-3 py-2" data-testid={`pb-row-${e.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{e.description}</div>
        <div className="text-[11px] text-muted-foreground truncate">Div {e.code || "—"} · per {e.unit} · {e.source}</div>
      </div>
      <RateInput value={r.labor_rate} onChange={(v) => setR({ ...r, labor_rate: v })} testid={`pb-labor-${e.id}`} />
      <RateInput value={r.material_rate} onChange={(v) => setR({ ...r, material_rate: v })} testid={`pb-material-${e.id}`} />
      <RateInput value={r.equipment_rate} onChange={(v) => setR({ ...r, equipment_rate: v })} testid={`pb-equip-${e.id}`} />
      <div className="text-right">
        <div className="font-mono text-sm tabular-nums">{money(unit)}</div>
        <button data-testid={`pb-verify-${e.id}`} onClick={() => patch({ verified: !e.verified }, e.verified ? "Unverified" : "Verified")}
          className={`text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1 ${e.verified ? "text-emerald-500" : "text-muted-foreground hover:text-foreground"}`}>
          <CheckCircle2 className="h-3 w-3" /> {e.verified ? "Verified" : "Verify"}
        </button>
      </div>
      <div className="flex items-center gap-1">
        {dirty && <button data-testid={`pb-save-${e.id}`} onClick={() => patch({ labor_rate: Number(r.labor_rate), material_rate: Number(r.material_rate), equipment_rate: Number(r.equipment_rate) }, "Saved")} disabled={busy}
          className="h-7 w-7 grid place-items-center rounded bg-primary text-primary-foreground hover:bg-orange-600"><Save className="h-3.5 w-3.5" /></button>}
        <button data-testid={`pb-del-${e.id}`} onClick={del} className="h-7 w-7 grid place-items-center rounded text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

export default function PriceBook() {
  const qc = useQueryClient();
  const [region, setRegion] = useState("");
  const [form, setForm] = useState({ description: "", unit: "ea", code: "", labor_rate: "", material_rate: "", equipment_rate: "" });
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["pricebook", region],
    queryFn: async () => (await api.get(`/price-book${region ? `?region=${encodeURIComponent(region)}` : ""}`)).data,
  });
  const regions = data?.regions || [];
  const entries = data?.entries || [];

  useEffect(() => { if (!region && regions.length) setRegion(regions[0]); }, [regions]); // eslint-disable-line

  const refresh = () => qc.invalidateQueries({ queryKey: ["pricebook"] });

  const add = async () => {
    if (!region.trim()) { toast.error("Enter a region first"); return; }
    if (!form.description.trim()) { toast.error("Enter a description"); return; }
    setAdding(true);
    try {
      await api.post(`/price-book`, { region: region.trim(), description: form.description.trim(), unit: form.unit || "ea", code: form.code,
        labor_rate: Number(form.labor_rate) || 0, material_rate: Number(form.material_rate) || 0, equipment_rate: Number(form.equipment_rate) || 0 });
      setForm({ description: "", unit: "ea", code: "", labor_rate: "", material_rate: "", equipment_rate: "" });
      refresh();
      toast.success("Rate saved to price book");
    } catch (e) { toast.error(apiErr(e)); } finally { setAdding(false); }
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="pricebook-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><BookMarked className="h-6 w-6 text-primary" /> Regional Price Book</h1>
        <p className="text-sm text-muted-foreground mt-1">Your verified local rates per region. Auto-filled from AI-priced estimates — reuse them on new bids with zero AI/credits via <span className="font-semibold">Price Book</span> on the Estimate tab.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex items-center gap-2 rounded-lg border border-input bg-popover px-3 py-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          {regions.length > 0 ? (
            <select data-testid="pb-region-select" value={region} onChange={(e) => setRegion(e.target.value)} className="bg-transparent text-sm outline-none">
              {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              {region && !regions.includes(region) && <option value={region}>{region}</option>}
            </select>
          ) : (
            <input data-testid="pb-region-input" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region (e.g. Austin, TX)" className="bg-transparent text-sm outline-none w-48" />
          )}
        </div>
        <span className="text-sm text-muted-foreground">{entries.length} saved rate(s){region ? ` for ${region}` : ""}</span>
      </div>

      {/* Manual add */}
      <div className="rounded-xl border border-border bg-card p-4 mb-5" data-testid="pb-add-box">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Add / update a rate</div>
        <div className="grid grid-cols-2 sm:grid-cols-[1fr_60px_60px_76px_76px_76px_auto] gap-2 items-center">
          <input data-testid="pb-add-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (e.g. Drywall hang & finish)" className="rounded-lg border border-input bg-popover px-3 py-2 text-sm outline-none focus:border-primary col-span-2 sm:col-span-1" />
          <input data-testid="pb-add-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="unit" className="rounded-lg border border-input bg-popover px-2 py-2 text-sm text-center outline-none focus:border-primary" />
          <input data-testid="pb-add-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Div" className="rounded-lg border border-input bg-popover px-2 py-2 text-sm text-center outline-none focus:border-primary" />
          <input data-testid="pb-add-labor" type="number" value={form.labor_rate} onChange={(e) => setForm({ ...form, labor_rate: e.target.value })} placeholder="Labor$" className="rounded-lg border border-input bg-popover px-2 py-2 text-sm font-mono text-right outline-none focus:border-primary" />
          <input data-testid="pb-add-material" type="number" value={form.material_rate} onChange={(e) => setForm({ ...form, material_rate: e.target.value })} placeholder="Matl$" className="rounded-lg border border-input bg-popover px-2 py-2 text-sm font-mono text-right outline-none focus:border-primary" />
          <input data-testid="pb-add-equip" type="number" value={form.equipment_rate} onChange={(e) => setForm({ ...form, equipment_rate: e.target.value })} placeholder="Equip$" className="rounded-lg border border-input bg-popover px-2 py-2 text-sm font-mono text-right outline-none focus:border-primary" />
          <button data-testid="pb-add-btn" onClick={add} disabled={adding} className="rounded-lg bg-primary text-primary-foreground px-3 py-2 font-semibold text-sm hover:bg-orange-600 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-1">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground text-sm">
          No saved rates{region ? ` for ${region}` : ""} yet. Generate an AI-priced estimate for a project in this region, or add rates above.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="pb-entries">
          <div className="grid grid-cols-[1fr_84px_84px_84px_90px_auto] gap-2 px-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            <span>Item</span><span className="text-center">Labor</span><span className="text-center">Material</span><span className="text-center">Equip</span><span className="text-right">Unit</span><span />
          </div>
          {entries.map((e) => <Row key={e.id} e={e} onSaved={refresh} />)}
        </div>
      )}
    </div>
  );
}
